import type {
  CancelPlanResult,
  OrgCard,
  PlanState,
  SetAutoRenewResult,
  UpgradePlanRequest,
  UpgradePlanResult,
} from "@/interfaces/tier";
import { apiClient } from "@/lib/apiClient";

/**
 * Fetch functions for the Tier API (DIV-7761/DIV-7811), mounted at /tier
 * (folded in from the earlier /plan stub per PR #146 review — one domain,
 * one set of endpoints). Wire shapes live in @/interfaces/tier. Mutation
 * business errors are HTTP 200 bodies with { status: "error" } — returned to
 * the caller, never thrown.
 */

/**
 * The caller's org tier/plan state — the app-wide tier source of truth (PRD F8).
 * Requires TIER view: Manager/View Only callers get a 403, which useTier turns
 * into the starter/free fallback.
 */
export async function fetchTierState(): Promise<PlanState> {
  const { data } = await apiClient.get<PlanState>("/tier");
  return data;
}

/** Starter → Pro upgrade. A decline resolves as { status: "error" } for inline display. */
export async function upgradePlan(body: UpgradePlanRequest): Promise<UpgradePlanResult> {
  const { data } = await apiClient.post<UpgradePlanResult>("/tier/upgrade", body);
  return data;
}

/** Schedules the downgrade at period end (Pro features stay active until `cancelAt`). */
export async function cancelPlan(): Promise<CancelPlanResult> {
  const { data } = await apiClient.post<CancelPlanResult>("/tier/cancel", {
    cancelAtPeriodEnd: true,
  });
  return data;
}

/**
 * The ORGANIZATION's saved card, or null. Distinct from the group payment methods the credits
 * flow uses: a subscription is billed to the organization's Stripe Customer, and Stripe will not
 * accept a card attached to a different one.
 */
export async function fetchSubscriptionCard(): Promise<OrgCard | null> {
  const { data } = await apiClient.get<{ card: OrgCard | null }>("/tier/payment-method");
  return data.card;
}

/** Mints a SetupIntent against the organization's Customer and returns its client secret. */
export async function createSubscriptionSetupIntent(): Promise<string> {
  const { data } = await apiClient.post<{ success: boolean; clientSecret: string }>(
    "/tier/setup-intent",
  );
  return data.clientSecret;
}

/** Turns auto-renew on/off; success echoes the new cancelAtPeriodEnd (= !autoRenew). */
export async function setPlanAutoRenew(autoRenew: boolean): Promise<SetAutoRenewResult> {
  const { data } = await apiClient.patch<SetAutoRenewResult>("/tier/auto-renew", { autoRenew });
  return data;
}
